import React, { useState, useRef, useEffect } from 'react'
import { Box, Button, Grid, Paper, Typography } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import ImageUploader from './ImageUploader'

const PhotoCompare: React.FC = () => {
  const [beforeImage, setBeforeImage] = useState<string>('')
  const [afterImage, setAfterImage] = useState<string>('')
  const [sliderPosition, setSliderPosition] = useState<number>(50)
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleBeforeUpload = (imageData: string) => {
    setBeforeImage(imageData)
  }

  const handleAfterUpload = (imageData: string) => {
    setAfterImage(imageData)
  }

  const handleMouseDown = () => {
    if (beforeImage && afterImage) {
      setIsDragging(true)
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !containerRef.current) return

    const rect = containerRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const percentage = (x / rect.width) * 100
    setSliderPosition(Math.max(0, Math.min(100, percentage)))
  }

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!containerRef.current) return

    const rect = containerRef.current.getBoundingClientRect()
    const x = e.touches[0].clientX - rect.left
    const percentage = (x / rect.width) * 100
    setSliderPosition(Math.max(0, Math.min(100, percentage)))
  }

  const handleReset = () => {
    setBeforeImage('')
    setAfterImage('')
    setSliderPosition(50)
  }

  const handleDownload = async () => {
    if (!containerRef.current || !beforeImage || !afterImage) return

    try {
      // Create a canvas to capture the comparison
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      const img1 = new Image()
      const img2 = new Image()

      img1.src = beforeImage
      img2.src = afterImage

      await Promise.all([
        new Promise((resolve) => { img1.onload = resolve }),
        new Promise((resolve) => { img2.onload = resolve })
      ])

      // Use the larger dimensions
      canvas.width = Math.max(img1.width, img2.width)
      canvas.height = Math.max(img1.height, img2.height)

      // Draw before image (left side)
      ctx.drawImage(img1, 0, 0, canvas.width, canvas.height)

      // Clip and draw after image (right side)
      const clipX = (sliderPosition / 100) * canvas.width
      ctx.save()
      ctx.beginPath()
      ctx.rect(clipX, 0, canvas.width - clipX, canvas.height)
      ctx.clip()
      ctx.drawImage(img2, 0, 0, canvas.width, canvas.height)
      ctx.restore()

      // Draw slider line
      ctx.strokeStyle = '#ffffff'
      ctx.lineWidth = 4
      ctx.beginPath()
      ctx.moveTo(clipX, 0)
      ctx.lineTo(clipX, canvas.height)
      ctx.stroke()

      // Download
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.download = 'photo-comparison.png'
          link.href = url
          link.click()
          URL.revokeObjectURL(url)
        }
      })
    } catch (error) {
      console.error('Error downloading comparison:', error)
      alert('Wystąpił błąd podczas pobierania porównania')
    }
  }

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [])

  const bothImagesLoaded = beforeImage && afterImage

  return (
    <Box>
      {/* Upload section */}
      {!bothImagesLoaded && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <ImageUploader
              label="Obraz 1 (Przed)"
              onImageUpload={handleBeforeUpload}
              currentImage={beforeImage}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ImageUploader
              label="Obraz 2 (Po)"
              onImageUpload={handleAfterUpload}
              currentImage={afterImage}
            />
          </Grid>
        </Grid>
      )}

      {/* Comparison view */}
      {bothImagesLoaded && (
        <Box>
          <Paper
            elevation={3}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 2,
              mb: 3,
              cursor: 'ew-resize',
              userSelect: 'none'
            }}
            ref={containerRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onTouchMove={handleTouchMove}
          >
            {/* Before image (full width) */}
            <Box
              component="img"
              src={beforeImage}
              alt="Before"
              sx={{
                width: '100%',
                height: 'auto',
                display: 'block',
                maxHeight: '600px',
                objectFit: 'contain'
              }}
            />

            {/* After image (clipped) */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                overflow: 'hidden',
                clipPath: `inset(0 0 0 ${sliderPosition}%)`
              }}
            >
              <Box
                component="img"
                src={afterImage}
                alt="After"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
              />
            </Box>

            {/* Slider line */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${sliderPosition}%`,
                width: '4px',
                bgcolor: 'white',
                boxShadow: '0 0 10px rgba(0,0,0,0.5)',
                cursor: 'ew-resize',
                zIndex: 10,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '40px',
                  height: '40px',
                  bgcolor: 'white',
                  borderRadius: '50%',
                  boxShadow: '0 0 10px rgba(0,0,0,0.3)',
                  border: '3px solid #2196F3'
                }
              }}
            />

            {/* Labels */}
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                bgcolor: 'rgba(0,0,0,0.6)',
                color: 'white',
                px: 2,
                py: 0.5,
                borderRadius: 1
              }}
            >
              Przed
            </Typography>
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                bgcolor: 'rgba(0,0,0,0.6)',
                color: 'white',
                px: 2,
                py: 0.5,
                borderRadius: 1
              }}
            >
              Po
            </Typography>
          </Paper>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
            >
              Pobierz porównanie
            </Button>
            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={handleReset}
            >
              Reset
            </Button>
          </Box>
        </Box>
      )}

      {/* Instructions */}
      {!bothImagesLoaded && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Załaduj oba obrazy, aby rozpocząć porównanie
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default PhotoCompare
